import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, X } from "lucide-react";

const STORAGE_KEY = "eyvo-demo-agent-help-fab-dismissed";

/**
 * Floating Help button that only appears on the user's *first* session
 * (i.e. while localStorage["eyvo-demo-agent-help-fab-dismissed"] is unset).
 *
 * Once the user clicks either the FAB itself or its small inline ✕,
 * we set the flag and the FAB is never shown again on this device.
 *
 * Mounted globally inside `<Layout />` so it floats over every authenticated
 * page.  It does NOT render on /help/* pages (no point promoting the help
 * page while the user is already inside it) and is hidden on the
 * `/share/:token` public view.
 */
export function HelpFAB() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return true; // SSR / no-storage → hide rather than spam
    }
  });

  useEffect(() => {
    // Hide on /help/* and /share/* without re-mounting.
    const path = window.location.pathname || "";
    if (path.startsWith("/help") || path.startsWith("/share")) {
      setDismissed(true);
    }
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }

  if (dismissed) return null;

  return (
    <div
      data-testid="help-fab"
      className="fixed bottom-6 right-6 z-40 flex items-end gap-2"
    >
      <div
        data-testid="help-fab-bubble"
        className="hidden max-w-xs items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs leading-snug text-slate-600 shadow-lg md:flex"
      >
        <span className="grid h-5 w-5 place-items-center rounded-full bg-indigo-100 text-indigo-700">
          <HelpCircle size={12} />
        </span>
        <span>
          New here?
          <Link
            to="/help/getting-started"
            data-testid="help-fab-link"
            onClick={dismiss}
            className="ml-1 font-semibold text-indigo-600 hover:underline"
          >
            Read the 60-second guide
          </Link>
          .
        </span>
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="help-fab-dismiss"
          onClick={dismiss}
          className="-mr-1 -mt-1 grid h-5 w-5 place-items-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X size={12} />
        </button>
      </div>
      <Link
        to="/help"
        data-testid="help-fab-button"
        onClick={dismiss}
        aria-label="Open Help Center"
        className="grid h-12 w-12 place-items-center rounded-full bg-indigo-600 text-white shadow-xl ring-1 ring-indigo-700/20 transition-transform hover:scale-105"
      >
        <HelpCircle size={22} strokeWidth={2.25} />
      </Link>
    </div>
  );
}
